import {
  batchCreateAge,
  batchCreateBasicField,
  batchCreateName,
  batchCreateNameSuper,
  batchDeleteElementAndSubs,
  batchMoveSurnameShareToNameSurn,
  batchRemoveOrUnSubSurname,
  batchRemoveOrUnSubSurnameExtension,
  createFieldValue,
  fieldToElementTypeMap,
  getImage,
  getNestedOrFloatingElementById,
  INDEXED_INCORRECTLY,
  isSharedField,
  sexTypes,
} from '@fs/zion-sls'
import { R2AppName } from '../../../../../../shared/services/config'
import { getNextUuid } from '../../../../../../shared/utils/utils'
import { submitEditEvents, getFieldIdAndValue, superDeduper } from './editPanelDispatchBuilders'

/**
 * Filters dispatches to only include those related to a specific highlight field
 * @param {Array} dispatches - The array of dispatches to filter
 * @param {Object} highlightsFieldInfo - Information about the highlight field
 * @param {Object} addSubDispatch - The ADD_SUB dispatch for the field
 * @returns {Array} Filtered array of dispatches
 */
const filterDispatchesForHighlightField = (dispatches, highlightsFieldInfo, addSubDispatch) => {
  return dispatches.filter((dispatch) => {
    return (
      dispatch.value.id === highlightsFieldInfo.id ||
      dispatch === addSubDispatch ||
      dispatch.value.id === addSubDispatch.value.superId ||
      dispatch.value.subId === addSubDispatch.value.superId
    )
  })
}

export const getCreateDispatchesForSingleEventField = ({
  values,
  images,
  selectedPersonId,
  highlightsFieldInfo,
  formState,
  types,
  addedEventId,
  eventTypeFieldId,
  currentImageArk,
}) => {
  const dispatches = submitEditEvents({
    values,
    images,
    selectedPersonId,
    formState,
    types,
    addedEventId,
    eventTypeFieldId,
    currentImageArk,
  })
  const addSubDispatch = dispatches.find((dispatch) => dispatch.value.subId === highlightsFieldInfo.id)
  return filterDispatchesForHighlightField(dispatches, highlightsFieldInfo, addSubDispatch)
}

export const handleDeleteField = (el, key, hasSecondarySurnameEl, selectedPerson, images) => {
  let dispatches = []
  if (el.fieldType === 'NAME_SURN') {
    const nameSurnameElement = getNestedOrFloatingElementById(
      [selectedPerson],
      el.superElements.find((superEl) => getNestedOrFloatingElementById([selectedPerson], superEl.id))?.id
    )
    if (hasSecondarySurnameEl) {
      // Only remove the NAME_SURN field
      if (el.superElements.length > 1) {
        dispatches.push({
          type: 'REM_SUB',
          value: {
            superPartitionKey: nameSurnameElement.partitionKey,
            subPartitionKey: el.partitionKey,
            superId: nameSurnameElement.id,
            subId: key,
            elementType: el.elementType,
          },
        })
      } else {
        dispatches.push({
          type: 'REMOVE_ELEMENT',
          value: {
            id: key,
            partitionKey: el.partitionKey,
            elementType: el.elementType,
          },
        })
      }
    } else {
      dispatches = superDeduper(
        dispatches.concat(batchRemoveOrUnSubSurname({ nameSurnameElement, person: selectedPerson, images }))
      )
    }
  } else if (el.fieldType === 'NAME_SPRE' || el.fieldType === 'NAME_PATR' || el.fieldType === 'NAME_MATR') {
    dispatches = superDeduper(
      dispatches.concat(batchRemoveOrUnSubSurnameExtension({ extensionField: el, person: selectedPerson, images }))
    )
  } else {
    dispatches = superDeduper(dispatches.concat(batchDeleteElementAndSubs({ element: el })))
  }
  return dispatches
}

/**
 * Get data about surname elements that takes into account being deleted and added.
 * @param formValues - methods.getValues of values from submit
 * @param selectedPerson - edited person object
 * @returns {{surnamePartitionKey: string, surnameElId: string, hasSecondarySurnameEl: boolean}}
 */
export const gatherSurnameInformation = (formValues, selectedPerson) => {
  let hasSecondarySurnameEl = false
  let surnameElId = ''
  let surnamePartitionKey = ''

  Object.entries(formValues).forEach(([key, value]) => {
    if (value === 'NAME_SPRE' || value === 'NAME_PATR' || value === 'NAME_MATR') {
      hasSecondarySurnameEl = true
    }
    if (value === 'NAME_SURN') {
      surnameElId = getNextUuid()
      surnamePartitionKey = selectedPerson.partitionKey
    }
    const el = getNestedOrFloatingElementById([selectedPerson], key)
    if (el) {
      if (
        value !== 'REMOVED' &&
        (el.fieldType === 'NAME_SPRE' || el.fieldType === 'NAME_PATR' || el.fieldType === 'NAME_MATR')
      ) {
        hasSecondarySurnameEl = true
      } else if (el.fieldType === 'NAME_SURN') {
        el.superElements.forEach((superEl) => {
          if (getNestedOrFloatingElementById([selectedPerson], superEl.id)) {
            surnameElId = superEl.id
            surnamePartitionKey = superEl.partitionKey
          }
        })
      }
    }
  })

  return { hasSecondarySurnameEl, surnameElId, surnamePartitionKey }
}

export const submitEditFields = ({ values, images, selectedPersonId, inPanelStack, formState, currentImageArk }) => {
  let dispatches = []
  const currentImage = getImage(images, currentImageArk)
  const selectedPerson = getNestedOrFloatingElementById([currentImage], selectedPersonId)
  let createdAgeId = ''
  const { hasSecondarySurnameEl, surnameElId, surnamePartitionKey } = gatherSurnameInformation(values, selectedPerson)

  Object.entries(values).forEach(([key, value]) => {
    const attribution = { comment: '', reasonId: INDEXED_INCORRECTLY, app: R2AppName }
    const el = getNestedOrFloatingElementById([selectedPerson], key)
    // the field already exists - either remove it or update its value
    if (el) {
      if (value === 'REMOVED' || value?.key === 'REMOVED') {
        dispatches = dispatches.concat(handleDeleteField(el, key, hasSecondarySurnameEl, selectedPerson, images))
      } else if (formState.dirtyFields[key]) {
        if (el.elementType === 'NAME') {
          dispatches.push({
            type: 'SET_NAME_TYPE',
            value: {
              id: key,
              partitionKey: selectedPerson.partitionKey,
              nameType: value,
            },
          })
        } else {
          const { id, value: fieldValue } = getFieldIdAndValue({ el, value, attribution })
          dispatches.push({ type: 'ADD_FV', value: { partitionKey: el.partitionKey, id, fieldValue } })
        }
      }
    } else if (key === 'primaryRadio') {
      if (formState.dirtyFields[key]) {
        dispatches.push({
          type: 'SET_PRIMARY',
          value: {
            id: selectedPersonId,
            partitionKey: selectedPerson.partitionKey,
            primary: value === 'yes',
          },
        })
      }
    } else if (
      !key.includes('fieldType') &&
      !key.includes('nameField') &&
      !key.includes('nameSuper') &&
      !key.includes('newNameElement') &&
      !Object.keys(values).includes(`${key}_nameField`) &&
      !Object.keys(values).includes(`${key}_newNameElement`)
    ) {
      if (value !== 'REMOVED') {
        // create new age field and possibly intermediary AGE element as well
        // we use the form to hold metadata about this field -
        // (e.g. {'<id>': '<value>', '<id>_fieldType': 'AGE'})
        let fieldValue = createFieldValue(value, attribution)

        const isSexField = sexTypes.includes(value?.key)
        if (isSexField) {
          fieldValue = createFieldValue(value.primaryText, attribution, `http://gedcomx.org/${value.key}`)
        }
        const superId = inPanelStack('editAge')
          ? selectedPerson.subElements.find((se) => se.elementType === 'AGE')?.id || createdAgeId
          : selectedPerson.id
        const superElementType = inPanelStack('editAge') ? 'AGE' : 'PERSON'
        if (!superId && superElementType === 'AGE') {
          const batchCreateAgeParams = {
            partitionKey: selectedPerson.partitionKey,
            personId: selectedPerson.id,
            id: key,
            fieldType: values[`${key}_fieldType`],
            fieldValue,
          }
          dispatches = dispatches.concat(batchCreateAge(batchCreateAgeParams))
          const newAgeElement = dispatches.find((d) => d.type === 'CREATE_ELEMENT' && d.value.elementType === 'AGE')
          createdAgeId = newAgeElement.value.id
        } else {
          const valueExists =
            (typeof values[key] === 'string' && values) || (typeof values[key] === 'object' && values[key].key)
          if (valueExists) {
            // create any other type of field
            const batchCreateAdditionalFieldParams = {
              partitionKey: selectedPerson.partitionKey,
              superId,
              id: key,
              fieldType: values[`${key}_fieldType`],
              fieldValue,
              superElementType,
            }
            dispatches = dispatches.concat(batchCreateBasicField(batchCreateAdditionalFieldParams))
          }
        }
      }
    } else if (
      !key.includes('nameField') &&
      !key.includes('nameSuper') &&
      Object.keys(values).includes(`${key}_nameField`) &&
      value !== 'REMOVED'
    ) {
      // create structured name subElement (e.g. NAME_SURNAME + NAME_SURN)
      // we use the form to hold metadata about this name element -
      // (e.g. {'<id>': '<value>', '<id>_nameField': 'NAME_GN', '<id>_nameSuper': '<nameSuperId>'})
      const fieldType = values[`${key}_nameField`] // e.g. NAME_SURNAME
      const elementType = fieldToElementTypeMap[fieldType]
      const nameSuperId = values[`${key}_nameSuper`]
      const fieldValue = createFieldValue(value, attribution)
      if (fieldType === 'NAME_SPRE' || fieldType === 'NAME_PATR' || fieldType === 'NAME_MATR') {
        if (!surnameElId) {
          dispatches = dispatches.concat(
            batchCreateName({
              partitionKey: selectedPerson.partitionKey,
              nameSuperId,
              fieldValue,
              fieldType,
              elementType,
              nameFieldId: key,
            })
          )
        } else {
          if (isSharedField(surnameElId, [selectedPerson])) {
            // Switch share from NAME_SURNAME to NAME_SURN level. All fields will have new NAME_SURNAMES created that point to the same NAME_SURN
            const surnameEl = getNestedOrFloatingElementById([selectedPerson], surnameElId)
            dispatches = dispatches.concat(batchMoveSurnameShareToNameSurn(surnameEl))
          }
          const selectedPersonName = selectedPerson.subElements.find((se) => se.elementType === 'NAME')
          const newSurnameId = dispatches.find(
            (disp) => disp.type === 'ADD_SUB' && disp.value.superId === selectedPersonName.id
          )?.value.subId
          dispatches = dispatches.concat(
            batchCreateBasicField({
              partitionKey: surnamePartitionKey,
              superId: newSurnameId || surnameElId,
              fieldType,
              fieldValue,
              superElementType: 'NAME_SURNAME',
            })
          )
        }
      } else {
        dispatches = dispatches.concat(
          batchCreateName({
            partitionKey: selectedPerson.partitionKey,
            nameSuperId,
            fieldValue,
            fieldType,
            elementType,
            nameFieldId: key,
            nameElementId: elementType === 'NAME_SURNAME' ? surnameElId : getNextUuid(),
          })
        )
      }
    } else if (!key.includes('newNameElement') && Object.keys(values).includes(`${key}_newNameElement`)) {
      // create new NAME element
      dispatches = dispatches.concat(
        batchCreateNameSuper({
          partitionKey: selectedPerson.partitionKey,
          nameElementId: key,
          nameType: value,
          personId: selectedPerson.id,
        })
      )
    }
  })
  // Update all records with new person ids (editing shared fields can affect other records than the current one)
  // This is not optimal.  We should just gather the record ids then update those records only.
  images.forEach((image) => {
    image.subElements.forEach((record) => {
      dispatches.push({
        type: 'SET_RECORD_PERSON_IDS',
        value: record.id,
      })
    })
  })
  return dispatches
}

export const getCreateDispatchesForSingleField = ({
  values,
  images,
  selectedPersonId,
  highlightsFieldInfo,
  inPanelStack,
  formState,
  currentImageArk,
}) => {
  const dispatches = submitEditFields({
    values,
    images,
    selectedPersonId,
    inPanelStack,
    formState,
    currentImageArk,
  })
  const addSubDispatch = dispatches.find((dispatch) => dispatch.value.subId === highlightsFieldInfo.id)
  return filterDispatchesForHighlightField(dispatches, highlightsFieldInfo, addSubDispatch)
}

export function handleFieldChange(dispatch) {
  dispatch({ type: 'SET_ACTIVE_FIELD_ID', value: '' })
}

export function handleOnFocus({ element, r2Dispatch }) {
  const activeFieldId = element?.subElements ? element?.subElements?.[0]?.id : element.id
  r2Dispatch({ type: 'SET_ACTIVE_FIELD_ID', value: activeFieldId })
}

/**
 * Remove a field and all associated information from form values
 * Leaves the form in a clean state if you remove a field that wasn't in the form originally
 * @param field - field to remove
 * @param methods - ZionForm methods object
 */
export function removeFieldFromValues(field, methods) {
  if (typeof methods.formState.defaultValues[field.id] !== 'undefined') {
    methods.setValue(field.id, 'REMOVED')
  } else {
    const newValues = methods.getValues()
    Object.keys(newValues).forEach((key) => {
      if (key.includes(field.id)) delete newValues[key]
    })
    methods.reset(newValues)
  }
}
